// Gestiona el session_uuid (sessionStorage) y registra device + sesión al cargar la app.
// Fase 2: agrega heartbeat periódico (cada 30s mientras la tab está visible) y cierre
// de sesión via Beacon API en pagehide (ver trackingQueue.js).
//
// Reusa appConfig.apiUrl y apiFetch (mismo cliente HTTP que el resto del frontend,
// ver Z:\src\helpers\apiFetch.js y Z:\src\data\config.js).

import appConfig from "../data/config";
import { apiFetch } from "../helpers/apiFetch";
import { getDeviceId, getDeviceInfo } from "./deviceId";
import { sendHeartbeat } from "./trackingQueue";

const SESSION_STORAGE_KEY = "dp_session_uuid";
const HEARTBEAT_INTERVAL_MS = 30000;

function generateUuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Obtiene el session_uuid actual (sessionStorage), generando uno nuevo si no existe.
 * sessionStorage se limpia automáticamente al cerrar la tab — cada tab tiene su propia sesión.
 */
function getSessionUuid() {
  try {
    let sessionUuid = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!sessionUuid) {
      sessionUuid = generateUuid();
      sessionStorage.setItem(SESSION_STORAGE_KEY, sessionUuid);
    }
    return sessionUuid;
  } catch {
    // sessionStorage no disponible — generamos uno efímero en memoria
    return generateUuid();
  }
}

/**
 * Registra el dispositivo en el backend (POST /tracking/device).
 * Si hay un token de auth, el backend linkea el device al user automáticamente.
 */
async function registerDevice(deviceId, token) {
  const { platform, browser, displayMode } = getDeviceInfo();

  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  try {
    await apiFetch(`${appConfig.apiUrl}/tracking/device`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        device_id: deviceId,
        platform,
        browser,
        display_mode: displayMode,
      }),
    });
  } catch {
    // Falla de red no debe romper la navegación — analytics no es crítico
  }
}

/**
 * Crea la sesión de tracking en el backend (POST /tracking/session/start).
 */
async function startSession(sessionUuid, deviceId) {
  try {
    await apiFetch(`${appConfig.apiUrl}/tracking/session/start`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        session_uuid: sessionUuid,
        device_id: deviceId,
        started_at: new Date().toISOString(),
      }),
    });
  } catch {
    // Falla de red no debe romper la navegación
  }
}

/**
 * Cierra la sesión de tracking actual (POST /tracking/session/end).
 * Llamar en logout manual. El cierre por tab-close se agrega en Fase 2 (Beacon API).
 */
export async function endSession(endReason = "manual") {
  const sessionUuid = getSessionUuid();

  stopHeartbeat();

  try {
    await apiFetch(`${appConfig.apiUrl}/tracking/session/end`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        session_uuid: sessionUuid,
        end_reason: endReason,
      }),
    });
  } catch {
    // Falla de red no debe bloquear el logout
  }
}

let heartbeatTimer = null;

/**
 * Arranca el heartbeat periódico (cada 30s) mientras la tab está visible.
 * Idempotente: si ya está corriendo, no crea un segundo interval.
 */
export function startHeartbeat(sessionUuid) {
  if (heartbeatTimer || !sessionUuid) return;

  heartbeatTimer = setInterval(() => {
    if (typeof document === "undefined" || document.visibilityState === "visible") {
      sendHeartbeat(sessionUuid, Math.round(HEARTBEAT_INTERVAL_MS / 1000));
    }
  }, HEARTBEAT_INTERVAL_MS);
}

/**
 * Detiene el heartbeat periódico (logout, fin de sesión).
 */
export function stopHeartbeat() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

/**
 * Punto de entrada: genera/recupera device_id + session_uuid, los registra en el backend,
 * y arranca el heartbeat periódico.
 * Llamar una vez al cargar la app (ej. en App.js).
 *
 * @param {string|null} token - token de auth actual (opcional, para linkear device al user)
 * @returns {Promise<{deviceId: string, sessionUuid: string}>}
 */
export async function initTrackingSession(token = null) {
  const deviceId = await getDeviceId();
  const sessionUuid = getSessionUuid();

  await registerDevice(deviceId, token);
  await startSession(sessionUuid, deviceId);
  startHeartbeat(sessionUuid);

  return { deviceId, sessionUuid };
}

export { getSessionUuid };
